/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import CustomGrid from 'components/grids/CustomGrid';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  StatusDot,
} from 'components/layouts/ContentLayout';
import { findSetupProjectStatusList } from 'apis/setup/home/SetupProjectStatus';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { CommonText } from 'ui/theme/Color';
import dayjs from 'dayjs';
import { CommonUtil } from 'utils/CommonUtil';

const SetupProjectStatusPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();

  const statusGridRef = useRef<any>();
  const [rowData, setRowData] = useState<any[]>([]);
  const [useTrafficLight, setUseTrafficLight] = useState<boolean>(false);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'plntNm',
        header: String(t('com.label.플랜트', '플랜트')),
        width: 130,
        align: 'center',
        cellTemplate: ({ value }) => (value ? `<span class="${value}">${value}</span>` : ''),
      },
      {
        binding: 'elmPrjNm',
        header: String(t('com.label.프로젝트명', '프로젝트명')),
        width: '*',
        align: 'left',
      },
      {
        binding: 'prdnCapa',
        header: String(t('com.label.생산 Capa\n(GWh)', '생산 Capa\n(GWh)')),
        width: 80,
        align: 'center',
      },
      {
        header: String(t('com.label.라인수', '라인수')),
        columns: [
          {
            binding: 'eltrEqpQty',
            header: String(t('com.label.전극', '전극')),
            width: 65,
            align: 'center',
            cellTemplate: (item) =>
              `<span style="color: ${CommonText.Primary};">${item.value || 0}</span>`,
          },
          {
            binding: 'asyEqpQty',
            header: String(t('com.label.조립', '조립')),
            width: 65,
            align: 'center',
            cellTemplate: (item) =>
              `<span style="color: ${CommonText.Primary};">${item.value || 0}</span>`,
          },
          {
            binding: 'mduEqpQty',
            header: String(t('com.label.모듈', '모듈')),
            width: 65,
            align: 'center',
            cellTemplate: (item) =>
              `<span style="color: ${CommonText.Primary};">${item.value || 0}</span>`,
          },
        ],
      },
      {
        header: String(t('com.label.진행율', '진행율')),
        columns: [
          {
            binding: useTrafficLight ? 'prjSt' : 'progRt',
            header: String(t('com.label.전체', '전체')),
            width: 65,
            align: 'center',
            cssClass: 'WijmoDot',
            cellTemplate: (item) =>
              useTrafficLight
                ? getTrafficLight(item)
                : `<p style="color: ${CommonText.Primary};">
                    ${item.value > 0.1 ? Number(item.value).toFixed(1) : 0}%
                  </p>`,
          },
          {
            binding: useTrafficLight ? 'eltrSt' : 'eltrRt',
            header: String(t('com.label.전극', '전극')),
            width: 65,
            align: 'center',
            cssClass: 'WijmoDot',
            cellTemplate: (item) =>
              useTrafficLight
                ? getTrafficLight(item)
                : `${item.value > 0.1 ? Number(item.value).toFixed(1) : 0}%`,
          },
          {
            binding: useTrafficLight ? 'asySt' : 'asyRt',
            header: String(t('com.label.조립', '조립')),
            width: 65,
            align: 'center',
            cssClass: 'WijmoDot',
            cellTemplate: (item) =>
              useTrafficLight
                ? getTrafficLight(item)
                : `${item.value > 0.1 ? Number(item.value).toFixed(1) : 0}%`,
          },
          {
            binding: useTrafficLight ? 'fmtSt' : 'fmtRt',
            header: String(t('com.label.활성화', '활성화')),
            width: 75,
            align: 'center',
            cssClass: 'WijmoDot',
            cellTemplate: (item) =>
              useTrafficLight
                ? getTrafficLight(item)
                : `${item.value > 0.1 ? Number(item.value).toFixed(1) : 0}%`,
          },
          {
            binding: useTrafficLight ? 'mduSt' : 'mduRt',
            header: String(t('com.label.모듈', '모듈')),
            width: 65,
            align: 'center',
            cssClass: 'WijmoDot',
            cellTemplate: (item) =>
              useTrafficLight
                ? getTrafficLight(item)
                : `${item.value > 0.1 ? Number(item.value).toFixed(1) : 0}%`,
          },
        ],
      },
      {
        header: String(t('com.label.일정', '일정')),
        columns: [
          {
            header: String(t('com.label.전극', '전극')),
            align: 'center',
            columns: [
              {
                binding: 'eltrSmpDt',
                header: String(t('com.label.샘플', '샘플')),
                width: 135,
                align: 'center',
                format: 'yyyy.MM.dd',
                cellTemplate: (params) =>
                  params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
              },
              {
                binding: 'eltrPrdnDt',
                header: String(t('com.label.양산', '양산')),
                width: 135,
                align: 'center',
                format: 'yyyy.MM.dd',
                cellTemplate: (params) =>
                  params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
              },
            ],
          },
          {
            header: String(t('com.label.Cell', 'Cell')),
            align: 'center',
            columns: [
              {
                binding: 'assySmpDt',
                header: String(t('com.label.샘플', '샘플')),
                width: 135,
                align: 'center',
                format: 'yyyy.MM.dd',
                cellTemplate: (params) =>
                  params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
              },
              {
                binding: 'assyPrdnDt',
                header: String(t('com.label.양산', '양산')),
                width: 135,
                align: 'center',
                format: 'yyyy.MM.dd',
                cellTemplate: (params) =>
                  params.value ? dayjs(params.value).format('YYYY.MM.DD') : '',
              },
            ],
          },
        ],
      },
    ];
  }, [useTrafficLight]);

  const getTrafficLight = (item: any) => {
    if (item != null) {
      if (item.value === 'ISSUE') {
        return `<span class="error"></span>`;
      }
      if (item.value === 'WARNING') {
        return `<span class="warning"></span>`;
      }
      return `<span class="confirm"></span>`;
    }
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      statusGridRef.current,
      t('setup.label.프로젝트 현황', '프로젝트 현황'),
      t('setup.label.프로젝트 현황', '프로젝트 현황')
    );
  };

  const onInitialized = (grid) => {
    statusGridRef.current = grid;
    grid.formatItem.addHandler((s, e) => {
      // 컬럼 헤더 패널만 처리
      if (e.panel === s.columnHeaders) {
        const text = e.cell.textContent ?? '';
        if (text.includes('\n')) {
          //줄바꿈
          e.cell.innerHTML = text.replace(/\n/g, '<br/>');
        }
      }
    });
  };

  const init = () => {
    findSetupProjectStatusList().then((res) => {
      setRowData(res?.map((o) => ({ ...o })) || []);
    });
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.프로젝트 현황', '프로젝트 현황')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <StatusDot>
            <span className="green">{t('com.label.On-going', 'On-going')}</span>
            <span className="yellow">{t('com.label.개선관리중', '개선관리중')}</span>
            <span className="red">{t('com.label.Issue', 'Issue')}</span>
          </StatusDot>
          <Button
            css={IconButton.button}
            className="icoNone"
            onClick={() => {
              setUseTrafficLight(!useTrafficLight);
            }}
            disableRipple
          >
            {useTrafficLight
              ? t('com.button.신호등 OFF', '신호등 OFF')
              : t('com.button.신호등 ON', '신호등 ON')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={init} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        isReadOnly={true}
        initialized={onInitialized}
        height={570}
      />
    </>
  );
};

export default SetupProjectStatusPage;
